import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Search, Loader2, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLE_LABELS = { super_duper_admin: "Super-Duper Admin", super_admin: "Super Admin", finance: "Finance", kiosk_manager: "Kiosk Manager" };
const METHOD_LABELS = { password: "Password", google: "Google", google_2fa: "Google · 2FA" };
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function Avatar({ name }) {
  const init = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{init}</div>;
}

export default function LoginActivity() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/users/login-events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return events;
    return events.filter((e) => `${e.email || ""} ${e.name || ""} ${e.ip || ""} ${e.method || ""}`.toLowerCase().includes(ql));
  }, [events, q]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="login-activity-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--brand-primary, #4f46e5)" }}><Activity className="h-5 w-5" /></div>
          <div>
            <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Login Activity</h1>
            <p className="text-slate-500 mt-0.5">Estate-wide record of who signed in, when and how.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="login-activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, IP…" className="pl-8 h-9 w-64" />
          </div>
          <Button data-testid="login-activity-refresh" variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>IP address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`login-event-${e.id}`}>
                  <TableCell className="text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">{fmtDateTime(e.at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar name={e.name} />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white leading-tight">{e.name || "—"}</p>
                        <p className="text-xs text-slate-400">{e.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary">{ROLE_LABELS[e.role] || e.role || "—"}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600 dark:text-slate-300">{METHOD_LABELS[e.method] || e.method}</TableCell>
                  <TableCell className="text-xs font-mono-x text-slate-400">{e.ip || "—"}</TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="login-activity-empty">No login activity recorded yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
      <p className="text-xs text-slate-400">Showing the most recent {events.length} sign-ins across all admin accounts.</p>
    </div>
  );
}
